import { AlertTriangle, Check, RefreshCw01 } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { QE_PHOTO_COPY } from "./copy"
import type { QEPhoto } from "./types"

// Quick Estimate 3 — quality tile (photos step).
//
// On the photos step the only question is "did this shot come out
// usable?" — side tagging is reviewed on the next step. So the tile
// surfaces the AI's quality verdict and nothing else.
//
// Footer color encodes verdict:
//   • emerald → looks clean (passed)
//   • amber   → needs another look (low quality, retake recommended)
//   • rose    → AI couldn't review (network / corruption — retry path)
//   • zinc    → kept anyway despite a quality flag
//
// Whole card is one tap target — image and footer both open the
// quality editor, so the row reads as a clickable list item, not a
// decorative caption.

interface QualityTileProps {
  photo: QEPhoto
  onOpen: () => void
}

export function QualityTile({ photo, onOpen }: QualityTileProps) {
  const failed = photo.verdict === "eval-failed"
  const kept = !!photo.sendWithoutAiTag
  const lowQuality =
    !failed &&
    !kept &&
    photo.uploadedUrl.length > 0 &&
    !photo.qualified

  const tone: "good" | "low" | "failed" | "kept" = failed
    ? "failed"
    : kept
      ? "kept"
      : lowQuality
        ? "low"
        : "good"

  const dotClass = {
    good: "bg-emerald-500",
    low: "bg-amber-500",
    failed: "bg-rose-500",
    kept: "bg-zinc-400",
  }[tone]

  const labelClass = {
    good: "text-emerald-700",
    low: "text-amber-800",
    failed: "text-rose-700",
    kept: "text-zinc-600",
  }[tone]

  const footerClass = {
    good: "bg-emerald-50/70 hover:bg-emerald-100/70 border-emerald-100",
    low: "bg-amber-50/80 hover:bg-amber-100/80 border-amber-100",
    failed: "bg-rose-50 hover:bg-rose-100/80 border-rose-100",
    kept: "bg-zinc-50 hover:bg-zinc-100/80 border-zinc-100",
  }[tone]

  const borderClass = {
    good: "border-emerald-200/70",
    low: "border-amber-200/80",
    failed: "border-rose-200/80",
    kept: "border-zinc-200/80",
  }[tone]

  const labelText =
    tone === "good"
      ? QE_PHOTO_COPY.verdictGood
      : tone === "kept"
        ? QE_PHOTO_COPY.useAnyway
        : tone === "failed"
          ? QE_PHOTO_COPY.verdictFailed
          : QE_PHOTO_COPY.verdictRetake

  return (
    <div
      className={cx(
        "rounded-2xl overflow-hidden border bg-white shadow-sm flex flex-col",
        borderClass,
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label="Inspect photo"
        className="relative aspect-square w-full bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
      >
        <img
          src={photo.previewUrl}
          alt="Captured photo"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {(tone === "failed" || tone === "low") && (
          <div
            className={cx(
              "absolute top-2 left-2 size-6 rounded-full grid place-items-center shadow ring-1 ring-white/40",
              tone === "failed" ? "bg-rose-500/95" : "bg-amber-500/95",
            )}
            aria-hidden
          >
            <AlertTriangle
              className="size-3 text-white"
              strokeWidth={2.5}
              aria-hidden
            />
          </div>
        )}
        {tone === "good" && (
          <div
            className="absolute top-2 left-2 size-6 rounded-full bg-emerald-500/95 grid place-items-center shadow ring-1 ring-white/40"
            aria-hidden
          >
            <Check className="size-3 text-white" strokeWidth={3} aria-hidden />
          </div>
        )}
      </button>

      <button
        type="button"
        onClick={onOpen}
        aria-label="Inspect photo"
        className={cx(
          "flex items-center justify-between gap-2 px-2.5 py-2 border-t",
          "transition-colors duration-150",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500",
          footerClass,
        )}
      >
        <span className="flex items-center gap-1.5 min-w-0 flex-1">
          <span
            className={cx("size-1.5 rounded-full shrink-0", dotClass)}
            aria-hidden
          />
          <span
            className={cx(
              "text-[12px] font-semibold leading-none truncate",
              labelClass,
            )}
          >
            {labelText}
          </span>
        </span>
        {tone !== "good" && tone !== "kept" && (
          <span
            className={cx(
              "size-5 rounded-full grid place-items-center shrink-0",
              tone === "failed"
                ? "bg-rose-500 text-white"
                : "bg-amber-500 text-white",
            )}
            aria-hidden
          >
            <RefreshCw01 className="size-2.5" strokeWidth={2.5} aria-hidden />
          </span>
        )}
      </button>
    </div>
  )
}
